import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';

/**
 * S8: Accounting Control Enhancement - Document Posting Status
 * GET /api/documents/posting-status?clienteId=X&status=posted|pending|failed&from=2026-01-01&to=2026-03-31
 * 
 * Returns documents with their Cegid posting status from cegid_posting_log.
 * Provides a real indicator of "posted to Cegid" based on actual sync data.
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'GET') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  try {
    const supabaseAdmin = getSupabaseAdminClient();
    const { clienteId, status, from, to, limit: queryLimit } = req.query;

    // Fetch documents with their posting log entries
    let docsQuery = supabaseAdmin
      .from('documentos')
      .select('id, nombre, tipo, estado, fecha_subida, cliente_id, clientes(nombre_fiscal)')
      .order('fecha_subida', { ascending: false })
      .limit(Number(queryLimit) || 100);

    if (clienteId) {
      docsQuery = docsQuery.eq('cliente_id', clienteId as string);
    }
    if (from) {
      docsQuery = docsQuery.gte('fecha_subida', from as string);
    }
    if (to) {
      docsQuery = docsQuery.lte('fecha_subida', to as string);
    }

    const { data: documents, error: docsError } = await docsQuery;
    if (docsError) throw docsError;

    if (!documents || documents.length === 0) {
      return res.status(200).json({ success: true, data: [], stats: { total: 0, posted: 0, pending: 0, failed: 0, not_synced: 0 } });
    }

    // Fetch posting logs for these documents
    const docIds = documents.map((d: any) => d.id);
    const { data: postingLogs } = await supabaseAdmin
      .from('cegid_posting_log')
      .select('documento_id, status, cegid_entry_id, posting_date, total_amount, invoice_number, error_message, updated_at')
      .in('documento_id', docIds);

    // Build a map of documento_id -> latest posting status
    const postingMap = new Map<string, any>();
    for (const log of (postingLogs || [])) {
      const existing = postingMap.get(log.documento_id);
      if (!existing || new Date(log.updated_at) > new Date(existing.updated_at)) {
        postingMap.set(log.documento_id, log);
      }
    }

    // Enrich documents with posting status
    const enriched = documents.map((doc: any) => {
      const posting = postingMap.get(doc.id);
      return {
        ...doc,
        posting_status: posting ? posting.status : 'not_synced',
        cegid_entry_id: posting?.cegid_entry_id || null,
        posting_date: posting?.posting_date || null,
        invoice_number: posting?.invoice_number || null,
        posting_amount: posting?.total_amount || null,
        posting_error: posting?.error_message || null,
        last_posting_update: posting?.updated_at || null,
      };
    });

    // Filter by posting status if requested
    let filtered = enriched;
    if (status) {
      filtered = enriched.filter((d: any) => d.posting_status === status);
    }

    // Stats
    const stats = {
      total: enriched.length,
      posted: enriched.filter((d: any) => d.posting_status === 'posted').length,
      pending: enriched.filter((d: any) => d.posting_status === 'pending').length,
      failed: enriched.filter((d: any) => d.posting_status === 'failed').length,
      retrying: enriched.filter((d: any) => d.posting_status === 'retrying').length,
      not_synced: enriched.filter((d: any) => d.posting_status === 'not_synced').length,
    };

    return res.status(200).json({
      success: true,
      data: filtered,
      stats,
    });
  } catch (error: any) {
    console.error('[Posting Status] Error:', error);
    return res.status(500).json({ error: 'Error al obtener estado de contabilización.' });
  }
}

export default withAuth(handler, ['admin', 'asesor']);
